// Prices the instant-estimate calculator uses. The page shows a range, never a binding price.
// Change a number, commit, push, and the calculator uses it on the next deploy.
//
// Set September 2026 from California market rates, priced about 10-20% above market for a
// premium, white-glove service:
//   Dry van, California outbound: about $3.00-3.50/mi (LA to Dallas $3.20-3.40).
//   Box truck: $1.75-2.25/mi standard, $2.75-3.50 expedited; Q2 2026 average about $3.09.
//   Cargo / sprinter van: $1.50-2.50/mi expedited.
//   Junk removal, LA and Orange County: 1/2 truck $400-600, full truck $700-1,100.
// Review these every few months; freight rates move with the market.

export const RATES = {
  // Straight-line miles between ZIP codes times this is roughly the road miles.
  roadFactor: 1.2,
  // The estimate is shown as the middle price plus or minus this much (0.10 = 10%).
  spread: 0.1,
  // Round the range to the nearest this many dollars.
  roundTo: 25,

  freight: {
    // Smallest vehicle that fits the load is picked. Capacities are planning numbers, not specs.
    vehicles: [
      { id: "van", name: "Cargo van", maxPallets: 2, maxLbs: 3000, perMile: 2.25, minimum: 300 },
      { id: "box", name: "Box truck", maxPallets: 12, maxLbs: 10000, perMile: 3.25, minimum: 500 },
      { id: "semi", name: "Semi truck and trailer", maxPallets: 26, maxLbs: 44000, perMile: 3.5, minimum: 850 },
    ],
    // Runs shorter than this many miles cost more per mile (loading time is the same).
    shortHaulMiles: 250,
    shortHaulMultiplier: 1.25,
    liftgate: 125,        // per load
    residential: 100,     // delivery to a home instead of a dock
    rush: 0.2,            // rush / team drivers: +20%
  },

  // Household moves. Off on the site until the CPUC and FMCSA household goods permits are in
  // place (FEATURES.moving in site.js). California moves must stay at or under the CPUC Max 4
  // tariff maximums: reported 2026 maximum for hourly moves up to 100 miles is $234.39/hr for a
  // truck, driver and one helper, plus $84.09/hr per extra mover (2 movers $234.39, 3 movers
  // $318.48, 4 movers $402.57). Check the current tariff before switching moving on.
  moving: {
    localMiles: 50,       // at or under this many miles: priced by the hour
    homes: {
      studio: { label: "Studio", hours: 3, crew: 2, vehicle: "van" },
      one: { label: "1 bedroom", hours: 4, crew: 2, vehicle: "box" },
      two: { label: "2 bedrooms", hours: 6, crew: 3, vehicle: "box" },
      three: { label: "3 bedrooms", hours: 8, crew: 3, vehicle: "box" },
      four: { label: "4+ bedrooms", hours: 10, crew: 4, vehicle: "semi" },
    },
    hourly: { 2: 209, 3: 289, 4: 369 },   // crew plus truck, per hour (under the Max 4 caps above)
    minimumHours: 3,
    driveMph: 35,                          // local drive time is billed as crew time
    perMile: { van: 2.25, box: 3.25, semi: 3.5 },  // long-distance moves, on top of load/unload
    packing: 0.4,                          // full packing and wrapping: +40% of crew time
    stairsPerFlight: 100,                  // per flight, either end, no elevator
    heavyItem: 250,                        // piano, safe, pool table: each
  },

  // Junk removal. Off on the site until local hauling permits are confirmed (FEATURES.junk).
  junk: {
    loads: {
      min: { label: "A few items", price: 175 },
      quarter: { label: "1/4 truck", price: 325 },
      half: { label: "1/2 truck", price: 525 },
      threeQuarter: { label: "3/4 truck", price: 725 },
      full: { label: "Full truck", price: 925 },
    },
    mattress: 50,    // each
    appliance: 75,   // each, fridges and AC units (refrigerant)
    tire: 20,        // each
    sameDay: 100,
  },
};
